import { useLocation, Link } from "react-router-dom";
import { Users, Database, LogOut, Shield, Building2, Flame, Menu, X, BookMarked, MessageSquareWarning, Flag, LayoutGrid, FileCog, Image } from "lucide-react";
import { useState } from "react";

const NAV = [
  { label: "Learner Manager",   path: "/admin/learners",           icon: Users,                   testid: "nav-learners" },
  { label: "School Manager",    path: "/admin/schools",            icon: Building2,               testid: "nav-schools" },
  { label: "Fuel Registry",     path: "/admin/fuel-registry",      icon: LayoutGrid,              testid: "nav-fuel-registry" },
  { label: "Dataset Operations",path: "/admin/dataset-ops",        icon: FileCog,                 testid: "nav-dataset-ops" },
  { label: "Dataset Health",    path: "/admin/dataset-health",     icon: Database,                testid: "nav-dataset-health" },
  { label: "SVG Cache Manager", path: "/admin/svg-cache",          icon: Image,                   testid: "nav-svg-cache" },
  { label: "Pool Readiness",    path: "/admin/pool-readiness",     icon: Flame,                   testid: "nav-pool-readiness" },
  { label: "FLN Module",        path: "/admin/fln",                icon: BookMarked,              testid: "nav-fln" },
  { label: "Mitraa Responses",  path: "/admin/mitraa-responses",   icon: MessageSquareWarning,    testid: "nav-mitraa-responses" },
  { label: "Question Flags",    path: "/admin/question-flags",     icon: Flag,                    testid: "nav-question-flags" },
];

export default function AdminLayout({ children, onLogout }) {
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div
        className="flex items-center gap-2.5 px-5 py-5"
        style={{ borderBottom: "1px solid #1f2937" }}
      >
        <div
          className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
          style={{ background: "#2563eb" }}
        >
          <Shield size={14} className="text-white" />
        </div>
        <div>
          <div className="text-white text-sm font-bold leading-tight">Control Room</div>
          <div className="text-xs" style={{ color: "#475569" }}>Adiora Admin</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map(({ label, path, icon: Icon, testid }) => {
          const active = pathname.startsWith(path);
          return (
            <Link
              key={path}
              to={path}
              data-testid={testid}
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors no-underline"
              style={{
                background: active ? "rgba(37,99,235,0.18)" : "transparent",
                color: active ? "#93c5fd" : "#9ca3af",
                fontWeight: active ? 600 : 400,
                border: active ? "1px solid rgba(37,99,235,0.25)" : "1px solid transparent",
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = "#1f2937";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              <Icon size={16} />
              {label}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="px-3 py-4" style={{ borderTop: "1px solid #1f2937" }}>
        <button
          onClick={onLogout}
          className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm transition-colors"
          style={{ color: "#6b7280" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#1f2937";
            e.currentTarget.style.color = "#ef4444";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#6b7280";
          }}
          data-testid="admin-logout-btn"
        >
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Desktop sidebar — fixed, always visible on lg+ */}
      <aside
        className="hidden lg:fixed lg:flex lg:flex-col top-0 left-0 h-full z-10"
        style={{ width: 240, background: "#111827", borderRight: "1px solid #1f2937" }}
      >
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60"
            onClick={() => setSidebarOpen(false)}
          />
          {/* Drawer */}
          <aside
            className="relative flex flex-col z-50"
            style={{ width: 240, background: "#111827", borderRight: "1px solid #1f2937" }}
          >
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Mobile top bar */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3"
        style={{ background: "#111827", borderBottom: "1px solid #1f2937" }}
      >
        <button
          onClick={() => setSidebarOpen(true)}
          className="text-white p-1 rounded"
          data-testid="admin-mobile-menu-btn"
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6 rounded-md" style={{ background: "#2563eb" }}>
            <Shield size={12} className="text-white" />
          </div>
          <span className="text-white text-sm font-bold">Control Room</span>
        </div>
      </div>

      {/* Content area */}
      <main
        className="flex-1 overflow-auto"
        style={{ background: "#f9fafb", minHeight: "100vh" }}
      >
        {/* Spacer for mobile top bar */}
        <div className="lg:hidden h-14" />
        {/* Spacer for desktop sidebar */}
        <div className="hidden lg:block" style={{ marginLeft: 240 }}>
          {/* inner content via wrapper below */}
        </div>
        <div className="lg:ml-[240px]">
          {children}
        </div>
      </main>
    </div>
  );
}
